<script setup lang="ts">
import { computed, onUnmounted, watch, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useCreateSensorStore } from '@/store/createSensor'
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import SchemaMappingTable from './SchemaMappingTable.vue' 

const emit = defineEmits(['validate'])
const createStore = useCreateSensorStore()
const { suggestedSchemas, selectedSchema } = storeToRefs(createStore)
const { selectSchema, startCreatingSchema } = createStore

const dropdownOptions = computed(() => suggestedSchemas.value)

const headerDescription = computed(() => {
  if (suggestedSchemas.value.length === 1) return 'O sistema identificou automaticamente um modelo compatível.'
  return 'Identificamos múltiplos modelos compatíveis. Selecione o ideal.'
})

watch(suggestedSchemas, async (schemas) => {
  await nextTick()
  if (schemas.length === 1 && !selectedSchema.value) selectSchema(schemas[0])
}, { immediate: true })

const isValid = computed(() => !!selectedSchema.value)
watch(isValid, (val) => emit('validate', val), { immediate: true })

const handleSchemaSelect = (option: any) => { if(option) selectSchema(option) }

const safeSchemaMapping = computed((): Record<string, string> => {
  if (!selectedSchema.value || !selectedSchema.value.schema) return {}
  return selectedSchema.value.schema as unknown as Record<string, string>
})
</script>

<template>
  <div class="animate-fadeIn max-w-4xl mx-auto w-full">
    
    <div class="text-center mb-10">
      <h2 class="text-2xl font-bold text-slate-900 dark:text-white">Definição de Modelo</h2>
      <p class="text-body text-slate-500 dark:text-gray-400 mt-1">
        {{ headerDescription }}
      </p>
    </div>

    <div class="bg-white border border-stroke dark:border-strokedark dark:bg-boxdark rounded-sm shadow-default overflow-visible">
      
      <div v-if="suggestedSchemas.length > 1" class="p-6 border-b border-stroke dark:border-strokedark">
        <div class="w-full max-w-lg mx-auto">
          <label class="block text-sm font-semibold text-slate-700 dark:text-white mb-2">Selecione o Modelo</label>
          <Dropdown 
            name="schema_selector" 
            placeholder="Selecione..." 
            label-attribute="name" 
            :options="dropdownOptions" 
            :alreadySelectedOption="selectedSchema" 
            @option-selected="handleSchemaSelect" 
            class="w-full"
          />
        </div>
      </div>

      <div v-else-if="suggestedSchemas.length === 1" class="px-6 py-5 bg-gray-50 border-b border-stroke flex items-center justify-between dark:bg-meta-4 dark:border-strokedark">
         <div class="flex flex-col">
             <span class="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Modelo Identificado</span>
             <div class="flex items-center gap-2">
                 <div class="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-400">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                 </div>
                 <h3 class="text-lg font-bold text-slate-800 dark:text-white">
                     {{ suggestedSchemas[0]?.name }}
                 </h3>
             </div>
         </div>
         
         <span class="hidden sm:inline-block px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-full border border-blue-100 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-800">
            Compatível com o payload recebido do sensor.
         </span>
      </div>

      <div v-if="selectedSchema">
        <SchemaMappingTable 
            :mapping="safeSchemaMapping" 
            variant="embedded"
        />
      </div>
      
      <div class="bg-white p-4 flex justify-center items-center dark:bg-boxdark border-t border-stroke dark:border-strokedark">
         <button 
            @click="startCreatingSchema" 
            class="text-sm text-gray-500 hover:text-primary hover:underline transition-colors flex items-center gap-2 group" 
            type="button"
         >
          <span>O modelo não é este?</span>
          <span class="font-medium text-primary group-hover:underline">Criar manualmente</span>
        </button>
      </div>

    </div>
  </div>
</template>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
</style>